import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, ArrowRight, Rocket, Radio } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  "Everything in Premium",
  "Tax Clarity Hub — full IRS Schedule C business categories",
  "Business tab — full features",
  "Team boards, task assignment & overdue tracking",
  "SnapBudget Budget vs Actual & business tracking",
  "SendGrid email notification flows",
  "Priority support badge",
];

export default function SnapPaywall({ access, onUnlocked }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const startTrial = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/snapbudget/trial");
      if (data.ok) {
        toast.success("14-day free trial started — welcome to SnapBudget!");
        onUnlocked();
      } else {
        toast.error(data.detail || "Trial unavailable");
        setBusy(false);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "platinum_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const subscribeBundle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "platinum_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const trialUsed = access?.trial_used;
  const isBusiness = user?.business_active;

  return (
    <div className="min-h-[70vh] grid place-items-center p-6">
      <div data-testid="snap-paywall" className="w-full max-w-lg bg-[#0d1730] border border-emerald-500/20 rounded-3xl p-8 sm:p-10 shadow-2xl shadow-emerald-950/50 text-center">
        <img src="/snapbudget-logo.webp" alt="SnapBudget" className="w-20 h-20 mx-auto rounded-2xl mb-5 shadow-lg shadow-emerald-900/40" />
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">SnapBudget <span className="text-emerald-400">Business Platinum</span></h1>
        <p className="text-slate-400 mt-2 text-sm">Part of Business Platinum — your full cashflow & books command center.</p>

        <div className="flex items-end justify-center gap-2 mt-6">
          <span className="text-5xl font-bold text-white">$69.99</span>
          <span className="text-slate-500 mb-2">/ month</span>
        </div>
        {!trialUsed && <p className="text-emerald-400 text-sm mt-1 font-medium">Start with a 7-day free trial · no charge today</p>}

        <ul className="text-left space-y-2.5 mt-6">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>

        <div className="mt-8 space-y-3">
          {isBusiness && (
            <button
              onClick={subscribeBundle}
              disabled={busy}
              data-testid="snap-bundle"
              className="group w-full h-12 rounded-xl bg-sky-500 text-white font-semibold flex items-center justify-center gap-2 hover:bg-sky-400 transition-colors disabled:opacity-50"
            >
              <Radio className="w-4 h-4" /> Upgrade to Business Platinum · $69.99/mo
            </button>
          )}
          {isBusiness && <p className="text-[11px] text-sky-300 -mt-1">Business bundle price — save $6/mo vs standalone.</p>}
          {!trialUsed && (
            <button
              onClick={startTrial}
              disabled={busy}
              data-testid="snap-start-trial"
              className="group w-full h-12 rounded-xl bg-emerald-500 text-[#04140c] font-semibold flex items-center justify-center gap-2 hover:bg-emerald-400 transition-colors disabled:opacity-50"
            >
              <Rocket className="w-4 h-4" /> {busy ? "Starting…" : "Start 14-day free trial"}
            </button>
          )}
          <button
            onClick={subscribe}
            disabled={busy}
            data-testid="snap-subscribe"
            className="group w-full h-12 rounded-xl border border-emerald-500/40 text-emerald-300 font-medium flex items-center justify-center gap-2 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" /> {trialUsed ? "Upgrade · $69.99/mo" : "Skip trial & subscribe"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
        <p className="text-[11px] text-slate-600 mt-4">Secure checkout by Stripe. Test card 4242 4242 4242 4242.</p>
      </div>
    </div>
  );
}
